import { Component, Input, OnInit, Output, EventEmitter, SimpleChanges, OnDestroy, OnChanges, Inject } from '@angular/core';
import { tvdGroupIds, TvdValuePillar, TvdValuePillarToEroiIdMapping, TvdProgramToGroupIdMapping, TVDMetricGroupResponse, eroiIds, availablePrograms } from '../../types/tvd-types';
import { EroiMetricComponent } from '../eroi-metric/eroi-metric.component';
import { Subscription } from 'rxjs';
import { TVDService } from '../../services/tvd.service';
import { CurrencyUomService } from 'src/app/core/services/currency-uom.service';
import { TVDModel } from '../../types/tvd-model';
import { FiltersService } from 'src/app/core/services/filters.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { Division } from 'src/app/core/services/ebr-api-client/ebr-api-client.g';
import { UIPreferencesService } from 'src/app/core/services/ui-preferences.service';
import { FEATURE_FLAGS, FeatureFlags } from 'src/app/config/AppInitializer.factory';
import { TVDDistributorService } from '../../services/tvd-distributor.service';

@Component({
    selector: 'tvd-value-pillar-summary',
    templateUrl: './tvd-value-pillar-summary.component.html',
    styleUrls: ['./tvd-value-pillar-summary.component.scss'],
    standalone: false
})
export class TvdValuePillarSummaryComponent implements OnInit, OnDestroy, OnChanges {
  @Input() valuePillar:TvdValuePillar | undefined;
  @Input() noMetricsForValuePillar: boolean | undefined;
  @Input() isExpanded: boolean = false;
  @Input() selectedProgram: string | undefined = availablePrograms.ALL;
  @Input() makingSelectionsState: boolean = false;

  @Output() toggleRequested = new EventEmitter<void>();
  @Output() createCustomTileRequested = new EventEmitter<void>();

  private _valuePillarComponents: EroiMetricComponent[] = [];
  private displayInRoundedNumberSubscription: Subscription | undefined;
  private tvdSubscription: Subscription | undefined;
  private filterSubscription: Subscription | undefined;
  private seletedProgramSubscription: Subscription | undefined;
  private displayedCurrencyCodeSubscription: Subscription | undefined;

  tvdData: TVDModel | undefined;
  valuePillarTotal: string | undefined = "0";
  valuePillarDefaultTotal: string | undefined = "0";
  currencyCode: string = "USD";
  valuePillarColor: string = "";
  valuePillarBorder: string = "";
  isLoading: boolean = true;
  filters: ContractModel | undefined;
  groupId: tvdGroupIds | undefined;
  division: string | undefined;
  displayInRoundedNumber: boolean = true;
  customTilesAdded: boolean = false;
  showDefaultTotal: boolean = false;

  // expose enum to html
  Division = Division;
  availablePrograms = availablePrograms;

  constructor(
    private _filtersService: FiltersService,
    private _tvdService: TVDService,
    private _tvdDistributorService: TVDDistributorService,
    private _currencyUomService: CurrencyUomService,
    private _uiPreferencesService: UIPreferencesService,
    @Inject(FEATURE_FLAGS) private _featureFlags: FeatureFlags,
  ) { }

  get enableTVDForAPInstitutional() {
    return this._featureFlags.enableTVDForAPInstitutional;
  }

  ngOnInit(): void {
    this.filterSubscription = this._filtersService.selectedFiltersObservable.subscribe((t) => {
      this.filters = t;
      if(this.filters) {
        this.division = this.filters.division;
      }
    });

    if(this.filters?.division === Division.Distributor) {
      this.tvdSubscription = this._tvdDistributorService.tvdObservable.subscribe((t : TVDModel | undefined) => {
        this.tvdData = t;
        this.calculatePillarTotal();
      });
    } else {
      this.tvdSubscription = this._tvdService.tvdObservable.subscribe( async (t) => {
        this.tvdData = t;
        this.calculatePillarTotal();
      });
    }

    // Initialize cosmetics for the component
    this.displayInRoundedNumberSubscription = this._uiPreferencesService.displayRoundedValuesObservable.subscribe(f => this.displayInRoundedNumber = f);
    this.displayedCurrencyCodeSubscription = this._currencyUomService.displayedCurrency$.subscribe(() => {
      this.currencyCode = this._currencyUomService.getDisplayedCurrency();
    });
    this.valuePillarColor = this.mapValuePillarToColor(this.valuePillar);
    this.valuePillarBorder = this.mapValuePillarToBorder(this.valuePillar);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['selectedProgram']) {
      this.calculatePillarTotal();
    }
    if(changes['isExpanded'] || changes['makingSelectionsState']) {
      this.valuePillarBorder = this.mapValuePillarToBorder(this.valuePillar);
    }
    // Edge case - If a pillar didnt have tiles and custom tile was added and later removed collapse the pillar
    if((changes['noMetricsForValuePillar'] && this.noMetricsForValuePillar) || changes['selectedProgram']) {
      if(this.isExpanded) {
        this.toggleRequested.emit();
      }
    }
  }

  ngOnDestroy(): void {
    this.tvdSubscription?.unsubscribe();
    this.filterSubscription?.unsubscribe();
    this.displayInRoundedNumberSubscription?.unsubscribe();
    this.seletedProgramSubscription?.unsubscribe();
    this.displayedCurrencyCodeSubscription?.unsubscribe();
  }

  /**
   * Calculates the total for value pillar by summing up the individual TVD metrics and custom metrics
   * @returns Total of TVD metrics and custom metrics for value pillar
   */
  private calculatePillarTotal() {
    if(!this.tvdData || this.tvdData.tvdResponse.length <= 0) {
      return;
    }

    let total = 0;
    let modifiedTotal = 0;

    // Get metrics for this value pillar
    const indvMetricList =  this._tvdService.getMetricsForValuePillar(
      this.tvdData.tvdResponse,
      this.valuePillar!,
      this.division!,
      this.selectedProgram
    );

    total = indvMetricList.reduce((acc, metric) => {
      const rawContribution = metric.monetaryContribution ?? '0';

      const numericContribution =
        Number(String(rawContribution).replace(/,/g, '')) || 0;
      return acc + numericContribution;
    }, total);

    let isModified = false;
    modifiedTotal = indvMetricList.reduce((acc, metric) => {
      const modifiedMonetaryContribution = Number(metric.modifiedMonetaryContribution?.replace(/,/g, '')) || 0;
      if (modifiedMonetaryContribution > 0) {
        isModified = true;
      }

      // Even if cost assumptions are applied, there are metrics in the same pillar we cannot modify costs for, in such cases use monetaryContributions
      const monetaryContribution = modifiedMonetaryContribution > 0
        ? modifiedMonetaryContribution
        : Number(metric.monetaryContribution?.replace(/,/g, '')) || 0;

      return acc + monetaryContribution;
    }, modifiedTotal);

    // Add custom tile contributions for this value pillar
    const customTiles = this.tvdData.custTileDict ? Array.from(this.tvdData.custTileDict.values()).filter(tile => tile.valuePillar === this.valuePillar) : [];
    const customTileTotal = customTiles.reduce((acc, tile) => {
      const rawContribution = tile.cost ?? '0';
      return acc + (Number(String(rawContribution).replace(/,/g, '')) || 0);
    }, 0);

    if (customTileTotal > 0) {
      isModified = true;
    }

    modifiedTotal += customTileTotal;

    this.customTilesAdded = customTiles.length > 0;
    this.isLoading = false;
    this.valuePillarTotal = modifiedTotal > 0 ? modifiedTotal.toLocaleString() : total.toLocaleString();
    this.valuePillarDefaultTotal = modifiedTotal > 0 ? total.toLocaleString() : modifiedTotal.toLocaleString();
    this.showDefaultTotal = isModified;
  }


  /**
   * Sets the background color for the vertical bar on the value pillar title section.
   */
  private mapValuePillarToColor(valuePillar: TvdValuePillar | undefined): string {
    switch(valuePillar) {

      case TvdValuePillar.BusinessOutcomes:
        return "bg-gray";
      case TvdValuePillar.OperationalPerformance:
        return "bg-deep-blue";
      case TvdValuePillar.EnvironmentalImpact:
        return 'bg-neptune-color-green-500';
      default:
        return "bg-deep-blue"; // Default to black if no match
    }
  }

  /**
   * Sets the border styles for each value pillar tile. The left and right tiles have rounded borders on the outside edges,
   * while the middle tile has vertical borders on both sides to seperate it from the other two tiles.
   */
  private mapValuePillarToBorder(valuePillar: TvdValuePillar | undefined): string {
    switch(valuePillar) {
      case TvdValuePillar.BusinessOutcomes:
        return (this.isExpanded || this.makingSelectionsState) ? "rounded-tl-xl" : "rounded-l-xl";

      // Operational performance is the middle tile and has vertical seperating borders
      case TvdValuePillar.OperationalPerformance:
        return `
          relative
          before:content-[''] before:absolute before:left-0 before:top-[10%] before:h-[80%] before:w-px before:bg-white
          after:content-[''] after:absolute after:right-0 after:top-[10%] after:h-[80%] after:w-px after:bg-white
        `;

      case TvdValuePillar.EnvironmentalImpact:
        return (this.isExpanded || this.makingSelectionsState) ? "rounded-tr-xl" : "rounded-r-xl";

      default:
        return "";
    }
  }
}
